import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { Box, Chip, Collapse, Divider, Typography } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { NURSE_HIGHLIGHT_BG, NURSE_HIGHLIGHT_CLASS } from "@/components/staffing/WeeklyGapView/consts";
import { useFocusOnStaffClick } from "@/hooks/useFocusOnStaffClick";
import { NEUTRAL, STATUS_BADGE } from "@/theme/tokens";
import type { HoursSummaryEmployeeDayShift, NurseInShift } from "@/utils/models";
import { describeCostlyRun } from "./describeCostlyRun";
import { mergeConsecutiveShifts } from "./mergeConsecutiveShifts";

interface IProps {
	shifts: NurseInShift[];
	shiftHoursById?: Map<string, HoursSummaryEmployeeDayShift>;
	actualShiftIds?: Set<string>;
	searchText: string | null;
	onShiftDoubleClick?: (shift: NurseInShift) => void;
}

const ROW_HEIGHT = "28px";
const ROW_FONT_SIZE = "13px";
const ROW_TEXT_COLOR = "primary.dark";
const HOVER_BG = NEUTRAL[50];
const EXPANDED_MAX_HEIGHT = 100;

export const FutureLookingWeeklyOvertimeDayRecord: FC<IProps> = ({ shifts, shiftHoursById, actualShiftIds, searchText, onShiftDoubleClick }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const { scheduleHint, cancelHint } = useFocusOnStaffClick();

	const runs = useMemo(() => mergeConsecutiveShifts(shifts), [shifts]);
	const count = runs.length;
	const hasItems = count > 0;

	return (
		<Box sx={{ px: 1, borderRadius: "4px" }} data-testid="high-cost-shifts-row">
			<Box
				onClick={hasItems ? () => setIsExpanded((prev) => !prev) : undefined}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: hasItems ? "pointer" : "default",
					"&:hover": hasItems ? { bgcolor: HOVER_BG } : undefined,
					transition: "background-color 0.2s ease",
					borderRadius: "4px",
				}}
			>
				<Typography
					sx={{
						fontWeight: 400,
						fontSize: ROW_FONT_SIZE,
						color: ROW_TEXT_COLOR,
						height: ROW_HEIGHT,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						width: "100%",
						pt: "4px",
					}}
				>
					{`${t("costly.staff")} - ${count}`}
				</Typography>
				{hasItems && (
					<ArrowDownward
						sx={{
							color: "primary.main",
							transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.3s ease",
							fontSize: 16,
						}}
					/>
				)}
			</Box>
			<Collapse in={isExpanded}>
				<Box sx={{ maxHeight: EXPANDED_MAX_HEIGHT, overflowY: "auto" }} data-testid="high-cost-shifts-list">
					{runs.map((run) => {
						const { shift, isAgency, isActual, hoursDisplay, start, end } = describeCostlyRun(run, shiftHoursById, actualShiftIds);
						const displayText = hoursDisplay ? `${shift.name} · ${hoursDisplay}` : `${shift.name} · ${start}-${end}`;
						const baseTooltip = hoursDisplay ? `${shift.name} · ${hoursDisplay} (${start}-${end})` : `${shift.name} · ${start}-${end}`;
						const tooltipContent = isActual ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
								<span>{baseTooltip}</span>
								<Chip label={t("actual")} size="small" sx={{ height: "16px", fontSize: "10px", fontWeight: 600, bgcolor: "#E8F4FF", color: "#1A6FB8" }} />
							</Box>
						) : (
							baseTooltip
						);

						const isSearchMatch = !!searchText && shift.name === searchText;
						return (
							<Box
								key={shift.id}
								data-testid={`high-cost-shifts-item-${shift.id}`}
								data-nurse-id={shift.nurseId}
								className={isSearchMatch ? NURSE_HIGHLIGHT_CLASS : undefined}
								onClick={onShiftDoubleClick ? scheduleHint : undefined}
								onDoubleClick={
									onShiftDoubleClick
										? () => {
												cancelHint();
												onShiftDoubleClick(shift);
											}
										: undefined
								}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									cursor: onShiftDoubleClick ? "pointer" : "default",
									"&:hover": { bgcolor: HOVER_BG },
									[`&.${NURSE_HIGHLIGHT_CLASS}`]: { bgcolor: NURSE_HIGHLIGHT_BG },
									transition: "background-color 0.2s ease",
									borderRadius: "4px",
									py: "2px",
								}}
							>
								<Divider />
								<InHouseTooltip title={tooltipContent} placement="top">
									<Typography
										sx={{
											fontWeight: 400,
											fontSize: ROW_FONT_SIZE,
											color: ROW_TEXT_COLOR,
											flex: 1,
											minWidth: 0,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
											userSelect: "none",
										}}
									>
										{displayText}
									</Typography>
								</InHouseTooltip>
								<Chip
									label={isAgency ? t("agency") : t("ot")}
									size="small"
									sx={{
										height: "18px",
										fontSize: "11px",
										fontWeight: 600,
										bgcolor: isAgency ? STATUS_BADGE.WARN_BG : STATUS_BADGE.ERROR_BG,
										color: isAgency ? STATUS_BADGE.WARN_INK : STATUS_BADGE.ERROR_INK,
									}}
								/>
							</Box>
						);
					})}
				</Box>
			</Collapse>
			<Divider />
		</Box>
	);
};
